/**
 * Merchant location detail, modeled on the organization location detail page:
 * a properties card on the left, the map and the branding assets on the right.
 */

import React, { useCallback, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Unstable_Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BlockIcon from '@mui/icons-material/Block';
//@ts-ignore
import { Map, Marker } from 'react-leaflet/es';

import PageContainer from '../../components/Redesing/page-container';
import PageContent from '../../components/Redesing/page-content';
import CardView from '../../components/Redesing/card-view';
import { PropertyList } from '../../components/Redesing/property-list';
import { PropertyListItem } from '../../components/Redesing/property-list-item';
import PropertyListItemLink from '../../components/Redesing/property-list-item-link';
import { SeverityPill } from '../../components/Redesing/severity-pill';
import NoFilePlaceHolder from '../../components/Redesing/no-file-place-holder';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import MovaticCustomModal from '../../components/Modal/MovaticCustomModal';
import CustomAsyncToast from '../../components/Redesing/custom-async-toast';
import { GenericMapTileLayer } from '../../components/mapComponent';
import { merchantStoreMarker } from '../../components/mapMarker';
import {
  useMerchantStoreByIdRelayQuery,
  mapMerchantStore,
} from '../../hooks/merchantStores/query/V2';
import { useRemoveMerchantStoreMutation } from '../../hooks/merchantStores/mutation/V2';
import useOrganization from '../../hooks/use-organization';
import { history, openGoogleMapsCoordinates } from '../../utils';

const ColorSwatch = ({ label, value }: { label: string; value: string }) => (
  <PropertyListItem label={label}>
    {value ? (
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            backgroundColor: value,
            border: '1px solid',
            borderColor: 'divider',
          }}
        />
        <Typography variant="body2">{value}</Typography>
      </Stack>
    ) : (
      <Typography variant="body2">N/A</Typography>
    )}
  </PropertyListItem>
);

const ImagePreview = ({ label, url }: { label: string; url: string | null }) => (
  <Stack spacing={1}>
    <Typography variant="subtitle2">{label}</Typography>
    {url ? (
      <Box
        component="img"
        alt={label}
        src={url}
        sx={{
          maxWidth: '100%',
          maxHeight: 160,
          objectFit: 'contain',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      />
    ) : (
      <NoFilePlaceHolder />
    )}
  </Stack>
);

const MerchantStoreDetails = () => {
  const organizationInfo = useOrganization();
  const { merchantStoreId } = useParams<{ merchantStoreId: string }>();

  const { data, isLoading, refetch } = useMerchantStoreByIdRelayQuery(merchantStoreId, {
    enabled: Boolean(merchantStoreId),
  });
  const { removeMerchantStore } = useRemoveMerchantStoreMutation();
  const [retireModalOpen, setRetireModalOpen] = useState(false);

  const store = data ? mapMerchantStore(data) : undefined;
  const isRetired = store?.state === 'RETIRED';

  const retire = useCallback(async () => {
    setRetireModalOpen(false);

    const result = await CustomAsyncToast({
      promise: () =>
        removeMerchantStore(merchantStoreId).then((res) => {
          if (!res.isSuccess) {
            throw new Error(
              res.errors[0]?.message || 'Failed to retire merchant location'
            );
          }
          return true;
        }),
      successMessage: () => 'Merchant location retired',
      errorMessage: 'There was an error retiring the merchant location.',
      loadingMessage: 'Retiring merchant location...',
    });
    if (result) refetch();
  }, [merchantStoreId, removeMerchantStore, refetch]);

  if (!organizationInfo?.id) {
    return <Redirect to="/dashboard" />;
  }

  const hasCoordinates = store?.lat != null && store?.lng != null;

  return (
    <PageContainer isLoading={isLoading}>
      <PageContent
        backLink="/branded-app/merchant-locations"
        chipLabel="Merchant Location"
        contentTitle="Branded App"
        itemName={store?.name ?? ''}
        itemChipValue={store?.status}
        detailIcon={<StorefrontIcon />}
        itemActions={null}
        itemTopButtons={
          <IconButtonMenu
            buttons={[
              {
                label: 'Edit',
                startIcon: <EditIcon />,
                onClick: () =>
                  history.push(`/branded-app/merchant-locations/${merchantStoreId}/edit`),
              },
              {
                label: 'Retire',
                startIcon: <BlockIcon />,
                onClick: () => setRetireModalOpen(true),
                disabled: isRetired,
              },
            ]}
          />
        }
        currentTabV="general"
        handleTabsChange={() => {}}
        tabsList={[{ label: 'Details', value: 'general' }]}
        tabsChildren={
          <Grid container spacing={2}>
            <Grid xs={12} lg={4}>
              <CardView
                isLoading={isLoading}
                title="Merchant Details"
                content={
                  <PropertyList>
                    <PropertyListItem label="Name" value={store?.name ?? 'N/A'} />
                    <PropertyListItem label="Status">
                      <SeverityPill color={isRetired ? 'error' : 'success'}>
                        {store?.status ?? 'Unknown'}
                      </SeverityPill>
                    </PropertyListItem>
                    {store?.website_url ? (
                      <PropertyListItemLink
                        label="Website"
                        text={store.website_url}
                        onClick={() => window.open(store.website_url, '_blank')}
                      />
                    ) : (
                      <PropertyListItem label="Website" value="N/A" />
                    )}
                    {hasCoordinates ? (
                      <PropertyListItemLink
                        label="Lat/Long"
                        text={`${store?.lat}, ${store?.lng}`}
                        onClick={() => openGoogleMapsCoordinates(store?.lat, store?.lng)}
                      />
                    ) : (
                      <PropertyListItem label="Lat/Long" value="N/A" />
                    )}
                    <ColorSwatch
                      label="Primary Color"
                      value={store?.primary_color ?? ''}
                    />
                    <ColorSwatch
                      label="Secondary Color"
                      value={store?.secondary_color ?? ''}
                    />
                  </PropertyList>
                }
              />
            </Grid>
            <Grid xs={12} lg={8}>
              <Stack spacing={2}>
                {hasCoordinates && (
                  <Card>
                    <Map
                      center={[store?.lat, store?.lng]}
                      zoom={15}
                      style={{ height: '24em' }}
                    >
                      <Marker
                        position={[store?.lat, store?.lng]}
                        icon={merchantStoreMarker(store?.logo_url, store?.primary_color)}
                      />
                      <GenericMapTileLayer />
                    </Map>
                  </Card>
                )}
                <CardView
                  isLoading={isLoading}
                  title="Branding"
                  content={
                    <Stack direction="row" spacing={3} p={2} flexWrap="wrap" useFlexGap>
                      <ImagePreview label="Logo" url={store?.logo_url ?? null} />
                      <ImagePreview label="Banner" url={store?.banner_url ?? null} />
                    </Stack>
                  }
                />
              </Stack>
            </Grid>
          </Grid>
        }
      />

      <MovaticCustomModal
        open={retireModalOpen}
        onClose={() => setRetireModalOpen(false)}
        title="Retire merchant location?"
        content={
          <Typography variant="body2">
            {store?.name ? `"${store.name}" ` : 'This merchant location '}
            will stop appearing in the branded app.
          </Typography>
        }
        action={
          <IconButtonMenu
            buttons={[
              { label: 'Cancel', onClick: () => setRetireModalOpen(false) },
              { label: 'Retire', onClick: retire, startIcon: <BlockIcon /> },
            ]}
          />
        }
      />
    </PageContainer>
  );
};

export default MerchantStoreDetails;
